import { cn } from "@omni/utils/styles";
import React from "react";
import {
    ArrayPath,
    Control,
    FieldArrayWithId,
    FieldValues,
    UseFieldArrayAppend,
    UseFieldArrayRemove,
    UseFieldArrayUpdate,
    UseFormTrigger,
    useFieldArray,
    useFormContext
} from "react-hook-form";

import { FieldLabel } from "@/components/Form/Base/fieldLabel.component";
import { FormFeedback } from "@/components/Form/Base/formFeedback.component";
import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { FormText } from "@/components/Form/Base/formText.component";
import { MicroCopy } from "@/components/Form/Base/microCopy.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { useFormErrors } from "../../Utils/useFormErrors.hook";
import { isFormRequired } from "../formField.types";

type MicroCopyProps = {
    amountOfFields: number;
};

export type BaseFieldArrayProps<
    T extends FieldValues,
    TPath extends ArrayPath<T>
> = {
    name: TPath;
    label: string;
    hideLabel?: boolean;
    microCopy?: string | ((props: MicroCopyProps) => React.ReactNode);
    required: isFormRequired<T, TPath>;
};

export type BaseFieldArrayChildProps<T extends FieldValues> = {
    fields: FieldArrayWithId<T, ArrayPath<T>, "id">[];
    control: Control<T, object, T>;
    name: BaseFieldArrayProps<T, ArrayPath<T>>["name"];
    actions: {
        append: UseFieldArrayAppend<T, ArrayPath<T>>;
        remove: UseFieldArrayRemove;
        trigger: UseFormTrigger<T>;
        update: UseFieldArrayUpdate<T, ArrayPath<T>>;
    };
};

export type BaseFieldArrayPropsWithChildren<
    T extends FieldValues,
    TPath extends ArrayPath<T>
> = BaseFieldArrayProps<T, TPath> & {
    children: (props: BaseFieldArrayChildProps<T>) => React.ReactNode;
};

const BaseFieldArray = <T extends FieldValues, TPath extends ArrayPath<T>>({
    name,
    microCopy,
    label,
    hideLabel = false,
    required,
    children
}: BaseFieldArrayPropsWithChildren<T, TPath>) => {
    const { t } = useGranite();
    const { control, trigger } = useFormContext<T>();

    const { error } = useFormErrors<T>(name);

    const { fields, append, remove, update } = useFieldArray<T>({
        control,
        name
    });

    return (
        <FormGroup>
            <FieldLabel
                name={name}
                label={label}
                required={required}
                hideLabel={hideLabel}
            />
            {microCopy &&
                (microCopy instanceof Function ? (
                    microCopy({ amountOfFields: fields.length })
                ) : (
                    <FormText
                        inline={true}
                        color="default"
                    >
                        {microCopy}
                    </FormText>
                ))}
            {!microCopy && <MicroCopy fieldId={name} />}
            <div className={cn({ "is-invalid": !!error })}>
                {children({
                    fields,
                    control,
                    name,
                    actions: { append, remove, trigger, update }
                })}
            </div>
            {!!error?.message && (
                <FormFeedback>
                    {error.message(label.toLowerCase())(t)}
                </FormFeedback>
            )}
        </FormGroup>
    );
};

export { BaseFieldArray };
